import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import { Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import { getEmailSettings, updateEmailSettings, sendTestEmail, getEmailStatus } from '../services/transfersAPI';

const EmailSettingsPage = () => {
  const [settings, setSettings] = useState({
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: 'Material Tracker',
    isActive: false
  });
  const [status, setStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await getEmailSettings();
      setSettings(prev => ({ ...prev, ...data }));
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const fetchStatus = async () => {
    try {
      const data = await getEmailStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch status:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await updateEmailSettings(settings);
      setSuccess('Email settings updated successfully');
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await sendTestEmail(testEmail);
      setSuccess(`Test email sent to ${testEmail}. Check your inbox!`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Email Notification Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Configure SMTP settings for email notifications
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2, mb: 2 }}>{success}</Alert>}

      {/* Status Card */}
      {status && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: status.active ? 'success.light' : 'warning.light' }}>
          <Typography variant="body2">
            <strong>Status:</strong> {status.active ? '✅ Active' : '⚠️ Inactive'}
          </Typography>
          {status.lastTested && (
            <Typography variant="caption">
              Last Tested: {new Date(status.lastTested).toLocaleString()}
            </Typography>
          )}
        </Paper>
      )}

      {/* SMTP Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>SMTP Configuration</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="SMTP Host"
              name="smtpHost"
              value={settings.smtpHost}
              onChange={handleChange}
              placeholder="smtp.gmail.com"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="SMTP Port"
              name="smtpPort"
              value={settings.smtpPort}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Username"
              name="smtpUser"
              value={settings.smtpUser}
              onChange={handleChange}
              placeholder="your-email@gmail.com"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label="SMTP Password"
              name="smtpPassword"
              value={settings.smtpPassword}
              onChange={handleChange}
              placeholder="Your app password"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Email"
              name="fromEmail"
              value={settings.fromEmail}
              onChange={handleChange}
              placeholder="noreply@yourcompany.com"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Name"
              name="fromName"
              value={settings.fromName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.isActive}
                  onChange={handleChange}
                  name="isActive"
                />
              }
              label="Enable Email Notifications"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Save Settings
          </Button>
        </Box>
      </Paper>

      {/* Test Email */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Test Email</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Send a test email to verify your SMTP configuration
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            fullWidth
            type="email"
            label="Test Email Address"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
          />
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={handleTestEmail}
            disabled={loading}
          >
            Send Test
          </Button>
        </Box>
      </Paper>

      {/* Gmail Setup Instructions */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'info.light' }}>
        <Typography variant="h6" gutterBottom>Gmail Setup Instructions</Typography>
        <Typography variant="body2" component="div">
          <ol>
            <li>Go to your Google Account settings</li>
            <li>Enable 2-Step Verification</li>
            <li>Generate an App Password (Security → App Passwords)</li>
            <li>Use the 16-character app password (not your regular password)</li>
            <li>SMTP Host: smtp.gmail.com</li>
            <li>SMTP Port: 587</li>
          </ol>
        </Typography>
      </Paper>
    </Box>
  );
};

export default EmailSettingsPage;